import { useState, useMemo, useEffect } from 'react'
import { useDispatch } from 'react-redux'

import {
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, Typography, IconButton, MenuItem,
  Button, CircularProgress, TextField, Autocomplete, Switch, FormControlLabel
} from '@mui/material'
import { CloseRounded } from '@mui/icons-material'

import { NotificationToast } from '../../../components/tools'
import { loaderArea } from '../../../utils'

import { startExport, getDiscountCodes } from '../../../redux/actions'

import { usePromiseTracker } from 'react-promise-tracker'

import { DateTime } from 'luxon'

import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import theme from '../../../assets/styles/MuiTheme'

const ExportModal = (props) => {
  const {
    exportModalOpen = false, setExportModalOpen = () => { }, industryList = []
  } = props

  const dispatch = useDispatch()

  const { promiseInProgress: exportInProgress } = usePromiseTracker({ area: loaderArea.ASSESSMENT_EXPORT })
  const { promiseInProgress: fetchingCodes } = usePromiseTracker({ area: loaderArea.ADMIN_DISCOUNTS })

  const defaultExportFilter = {
    selectedIndustry: 'All',
    selectedSector: 'All',
    startDateTime: null,
    endDateTime: null,
    selectedCode: null,
    includeFullData: true
  }

  // Filter object for export
  const [exportFilter, setExportFilter] = useState({ ...defaultExportFilter })
  // Local array of discount codes for code select
  const [discountCodes, setDiscountCodes] = useState([])
  // Control value for code select
  const [inputValue, setInputValue] = useState('')

  // Set returned array of discount codes to local state
  const callback = (data) => {
    if (data?.length) setDiscountCodes(data)
  }

  // Get full array of discount codes for export select
  useEffect(() => {
    dispatch(getDiscountCodes({ callback, exportCodes: true }))
  }, [])

  // Set the sector list to the sectors of the selected industry
  const sectorOpts = useMemo(() => {
    if (industryList && exportFilter?.selectedIndustry) {
      const indSectors = exportFilter?.selectedIndustry?.sectors
      return indSectors
    }
    return []
  }, [industryList, exportFilter])

  // Handle the industry selection
  const handleIndustryChange = (id) => {
    // Reset the sector list
    setExportFilter({ ...exportFilter, selectedIndustry: 'All' })

    // find the selected ID in the industry list
    const newIndustry = industryList.find(x => x.industryID === parseInt(id))

    // If the industry was found
    if (newIndustry) {
      setExportFilter({ ...exportFilter, selectedIndustry: newIndustry })
    } else {
      // If no industry was found and the industry was not intentionally being unset, fire a notification
      if (id !== 'All') { NotificationToast(true, 'Unable to find selected industry.') }
      setExportFilter({ ...exportFilter, selectedIndustry: 'All', selectedSector: 'All' })
    }
  }

  const handleSectorChange = (id) => {
    // find the id in the list of sector opts
    const newSector = sectorOpts.find(x => x.sectorID === parseInt(id))

    // If the sector was found
    if (newSector) {
      setExportFilter({ ...exportFilter, selectedSector: newSector })
    } else {
      // If no sector was found and the id was not intentionally being unset, fire a notification
      if (id !== 'All') { NotificationToast(true, 'Unable to find selected sector.') }
      setExportFilter({ ...exportFilter, selectedSector: 'All' })
    }
  }

  // Reset state and close export modal
  const handleExportModalClose = () => {
    setInputValue('')
    setExportFilter({ ...defaultExportFilter })
    setExportModalOpen(false)
  }

  // Format value and dispatch export
  const handleStartExport = () => {
    const startValue = exportFilter?.startDateTime
    const endValue = exportFilter?.endDateTime
    const startValid = Boolean(!startValue?.invalid)
    const endValid = Boolean(!endValue?.invalid)

    // Show notification if there is a start value but it is invalid, or there is a start value that is valid but no valid end value
    if ((startValue && !startValid) || (startValue && startValid && (!endValue || (endValue && !endValid)))) {
      NotificationToast(true, 'Missing or invalid date/time ranges.')
      return false
    }

    const exportVals = {
      ...exportFilter,
      industryID: exportFilter?.selectedIndustry?.industryID || null,
      sectorID: exportFilter?.selectedSector?.sectorID || null,
      discountCode: exportFilter?.selectedCode?.code || null,
      startTime: startValue && startValid ? DateTime.fromObject(startValue?.c).toFormat('yyyy-MM-dd HH:mm:ss') : null,
      endTime: endValue && endValid ? DateTime.fromObject(endValue?.c).toFormat('yyyy-MM-dd HH:mm:ss') : null
    }

    dispatch(startExport({ exportVals, fireExportSuccess, fireExportFailure }))
  }

  const fireExportSuccess = (base64, fileName) => {
    // If the export was not selected to include full data a base64 string of the zipped excel file will be returned
    // If the base64 and fileName are correctly returned, proceed to download
    if (base64 && fileName) {
      const a = document.createElement('a')
      if (a) {
        a.href = `data:application/zip;base64,${base64}`
        a.download = fileName
        a.onclick = () => { return true }
        a.click()
        a.remove()
      }
    }

    // Conditionally display message based on returned data
    let message = 'Export downloaded successfully.'
    if (!base64 && !fileName) message = 'The export will be emailed to you shortly.'
    NotificationToast(false, message)

    handleExportModalClose()
  }

  const fireExportFailure = () => {
    handleExportModalClose()
    NotificationToast(true, 'Unable to start export.')
  }

  // Handle settings value for start and end range selects
  const handleDateTimeChange = (value, type) => {
    if ((type === 'start')) {
      if (value) {
        setExportFilter({ ...exportFilter, startDateTime: value })
      } else {
        setExportFilter({ ...exportFilter, startDateTime: null, endDateTime: null })
      }
    }

    if ((type === 'end')) {
      setExportFilter({ ...exportFilter, endDateTime: value || null })
    }
  }

  // Handle setting the selected discount code to the filter
  const handleCodeSelect = (event, value) => {
    setExportFilter({ ...exportFilter, selectedCode: value || null })
  }

  return (
    <Dialog
      open={exportModalOpen}
      onClose={() => handleExportModalClose()}
      fullWidth
      keepMounted
    >
      <DialogTitle>
        <Grid container direction='row' justifyContent='space-between' alignItems='center'>
          <Grid item>
            <Typography variant='h5'>Export</Typography>
          </Grid>

          <Grid item>
            <IconButton style={{ padding: '0px' }} onClick={() => handleExportModalClose()}>
              <CloseRounded />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent dividers p={1}>
        {fetchingCodes &&
        <Grid item container direction='row' justifyContent='center'>
          <CircularProgress />
        </Grid>}

        {!fetchingCodes &&
          <Grid item container direction='column' xs={12}>
            <Grid item container direction='row' ml={3}>
              <Typography variant='body2'>
                  {'Use the selections below to filter the export.'}
              </Typography>
            </Grid>
            <Grid item container direction='row' ml={3} mb={2}>
              <Typography variant='body2'>
                  {exportFilter?.includeFullData ? 'The export will be emailed to you shortly.' : 'The export will be downloaded immediately with only the first tab of data.'}
              </Typography>
            </Grid>

            <Grid item container direction='row' spacing={2} p={2}>
              <Grid item container direction='column' xs={6}>
                <TextField
                  select
                  label='Industry'
                  variant='outlined'
                  size='medium'
                  value={exportFilter?.selectedIndustry?.industryID || 'All'}
                  onChange={e => {
                    handleIndustryChange(e.target.value)
                  }}
                  sx={{ width: '100%', '.MuiInputBase-input': { padding: '1em' } }}
                >
                  <MenuItem value='All'>All</MenuItem>

                  {industryList?.length && industryList?.map(industry =>
                    <MenuItem key={`industry-${industry.industryID}`} value={industry.industryID}>{industry.industryName}</MenuItem>
                  )}
                </TextField>
              </Grid>
            
              <Grid item container direction='column' xs={6}>
                <TextField
                  select
                  label='Sector'
                  variant='outlined'
                  size='medium'
                  value={exportFilter?.selectedSector?.sectorID || 'All'}
                  disabled={!sectorOpts?.length}
                  onChange={e => {
                    handleSectorChange(e.target.value)
                  }}
                  sx={{ width: '100%', '.MuiSelect-select': { padding: '1em' } }}
                >
                  <MenuItem value='All'>All</MenuItem>

                  {sectorOpts && sectorOpts.length && sectorOpts.length > 0 && sectorOpts.map(sector =>
                    <MenuItem key={`sector-${sector.sectorID}`} value={sector.sectorID}>{sector.sectorName}</MenuItem>
                  )}
                </TextField>
              </Grid>
            </Grid>

            <Grid item container direction='row' spacing={2} p={2}>
              <Grid item container direction='column' xs={6}>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                  <DateTimePicker
                    label='Range Start'
                    inputFormat='MM/dd/yyyy hh:mm a'
                    ampmInClock
                    disableFuture
                    renderInput={(params) => <TextField variant='outlined' {...params} sx={{ width: '100%', '.MuiOutlinedInput-input': { padding: '1em' } }} />}
                    value={exportFilter?.startDateTime}
                    onChange={(value) => handleDateTimeChange(value, 'start')}
                    componentsProps={{
                      actionBar: {
                        actions: ['clear']
                      }
                    }}
                    sx={{ width: '100%', '.MuiOutlinedInput-root': { padding: '1em' } }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item container direction='column' xs={6}>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                  <DateTimePicker
                    label='Range End'
                    inputFormat='MM/dd/yyyy hh:mm a'
                    ampmInClock
                    disabled={!exportFilter?.startDateTime}
                    minDateTime={exportFilter?.startDateTime}
                    disableFuture
                    renderInput={(params) => <TextField variant='outlined' {...params} sx={{ width: '100%', '.MuiOutlinedInput-input': { padding: '1em' } }} />}
                    value={exportFilter?.endDateTime}
                    onChange={(value) => handleDateTimeChange(value, 'end')}
                    componentsProps={{
                      actionBar: {
                        actions: ['clear']
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>

            <Grid item container direction='row' spacing={2} p={2}>
              <Grid item container direction='column' xs={6}>
                <Autocomplete
                  freeSolo
                  size='medium'
                  options={discountCodes}
                  getOptionLabel={(option) => option?.code || ''}
                  value={exportFilter?.selectedCode}
                  inputValue={inputValue}
                  onChange={handleCodeSelect}
                  onInputChange={(event, newInputValue) => { setInputValue(newInputValue) }}
                  sx={{ width: '100%' }}
                  renderInput={(params) => <TextField {...params} label='Discount Code' sx={{ width: '100%', '.MuiInputBase-root': { padding: '.5em' } }} />}
                />
              </Grid>

              <Grid item container direction='column' xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={exportFilter?.includeFullData}
                      onChange={() => setExportFilter({ ...exportFilter, includeFullData: !exportFilter?.includeFullData })}
                      sx={{
                        '& .Mui-checked': {
                          '+ .MuiSwitch-track': { backgroundColor: theme.palette.brand.secondary.stone },
                          '& .MuiSwitch-thumb': { color: theme.palette.brand.primary.forest }
                        }
                      }}
                    />
                  } 
                  label='Include Full Data'
                />
              </Grid>
            </Grid>
          </Grid>}

        <DialogActions>
          <Grid container direction='row' justifyContent='flex-end' alignItems='center' sx={{ marginTop: '1em' }}>
            <Grid item>
              <Button
                variant='outlined'
                color='primary'
                style={{
                  margin: '.5em 0.5em',
                  fontWeight: '600'
                }}
                onClick={() => handleExportModalClose()}
              >
                Cancel
              </Button>
            </Grid>

            <Grid item>
              <Button
                onClick={(e) => { e.preventDefault(); handleStartExport() }}
                variant='contained'
                disabled={fetchingCodes}
                color='primary'
                style={{
                  margin: '.5em 0.5em',
                  fontWeight: '600'
                }}
              >
                {
                  exportInProgress ? <CircularProgress style={{ color: 'white' }} size={25} /> : 'Start Export'
                }
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </DialogContent>
    </Dialog>
  )
}

export default ExportModal
